export interface BrowserIdentity {
  handle: string;
  displayName: string;
  rootDid: string;
}

interface StoredBrowserIdentities {
  version: 1;
  identities: BrowserIdentity[];
}

const MAX_BROWSER_IDENTITIES = 8;

export class IdentityContinuityError extends Error {
  constructor() {
    super("This sign-in handle no longer matches the DID remembered by this browser.");
    this.name = "IdentityContinuityError";
  }
}

export function readBrowserIdentities(storage: Storage, key: string): BrowserIdentity[] {
  try {
    const parsed = JSON.parse(storage.getItem(key) ?? "null") as Partial<StoredBrowserIdentities> | null;
    if (parsed?.version !== 1 || !Array.isArray(parsed.identities)) return [];
    return parsed.identities.filter(isBrowserIdentity).slice(0, MAX_BROWSER_IDENTITIES);
  } catch {
    return [];
  }
}

export function rememberBrowserIdentity(storage: Storage, key: string, identity: BrowserIdentity): void {
  if (!isBrowserIdentity(identity)) return;
  const remembered = readBrowserIdentities(storage, key);
  if (remembered.some((item) => item.handle === identity.handle && item.rootDid !== identity.rootDid)) {
    throw new IdentityContinuityError();
  }
  const identities = [
    identity,
    ...remembered.filter((item) => item.rootDid !== identity.rootDid),
  ].slice(0, MAX_BROWSER_IDENTITIES);
  try {
    storage.setItem(key, JSON.stringify({ version: 1, identities } satisfies StoredBrowserIdentities));
  } catch {
    // This list is optional device convenience. Sign-in continues if storage is disabled.
  }
}

export function requireExpectedRootDid(expectedRootDid: string | undefined, actualRootDid: string): void {
  if (expectedRootDid && expectedRootDid !== actualRootDid) throw new IdentityContinuityError();
}

function isBrowserIdentity(value: unknown): value is BrowserIdentity {
  if (!value || typeof value !== "object") return false;
  const identity = value as Partial<BrowserIdentity>;
  return (
    typeof identity.handle === "string" && /^[a-z][a-z0-9-]{2,30}$/u.test(identity.handle) &&
    typeof identity.displayName === "string" && identity.displayName.length > 0 && identity.displayName.length <= 80 &&
    typeof identity.rootDid === "string" && /^did:vibe:p256:[A-Za-z0-9_-]{44}$/u.test(identity.rootDid)
  );
}
